import { useMutation } from 'urql'
import { gql } from '../gql/gql'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth-provider'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'
import { Logo } from '../components/logo'
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google'
import { validateName } from '../lib/utils'

const LOGIN_WITH_GOOGLE_MUTATION = gql(`
  mutation LoginWithGoogleSignup($idToken: String!) {
    loginWithGoogle(idToken: $idToken) {
      accessToken
      refreshToken
      user { id name email }
    }
  }
`)

const SIGNUP_MUTATION = gql(`
  mutation Signup($input: NewUserInput!) {
    signup(input: $input) {
      accessToken
      refreshToken
      user {
        id
        name
        email
      }
    }
  }
`)

export function SignupPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const { setAccessToken } = useAuth()
  // Cast to any to avoid TS error before codegen runs
  const [{ fetching }, signup] = useMutation(SIGNUP_MUTATION)
  const [, loginWithGoogle] = useMutation(LOGIN_WITH_GOOGLE_MUTATION)
  const navigate = useNavigate()

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setError('')
    const result = await loginWithGoogle({ idToken: credentialResponse.credential ?? '' })
    if (result.error) { setError(result.error.graphQLErrors[0]?.message ?? result.error.message); return }
    if (result.data?.loginWithGoogle) {
      setAccessToken(result.data.loginWithGoogle.accessToken)
      navigate('/dashboard')
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    const nameError = validateName(name)
    if (nameError) { setError(nameError); return }

    const result = await signup({ input: { name, email, password } })
    
    if (result.error) {
      // 1. Check for structured error code (Best Practice for i18n)
      const graphQLError = result.error.graphQLErrors[0]
      if (graphQLError?.extensions?.code === 'EMAIL_ALREADY_EXISTS') {
        setError('This email is already registered. Please sign in.')
        return
      }

      // 2. Fallback: Show the raw message (or map other known errors)
      // This handles cases where the backend hasn't been updated to send codes yet
      setError(result.error.graphQLErrors[0]?.message ?? result.error.message)
      return
    }

    if (result.data?.signup) {
      setAccessToken(result.data.signup.accessToken)
      navigate('/dashboard')
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-primary/5 dark:bg-gray-900 p-4 gap-8">
      <div className="scale-150">
        <Logo />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create an account</CardTitle>
          <CardDescription>Enter your details to get started</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="text-sm text-red-500 font-medium">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button type="submit" className="w-full" disabled={fetching}>
              {fetching ? 'Creating account...' : 'Sign up'}
            </Button>
            <div className="relative w-full flex items-center gap-2 py-1">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 border-t border-border" />
            </div>
            <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-in failed')} width="100%" />
            <Button variant="link" className="w-full" asChild>
              <Link to="/login">Already have an account? Sign in</Link>
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              By signing up, you agree to our{' '}
              <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}